import React, { useEffect, useMemo, useState } from "react";
import ContactUserIcon from "components/ContactUserIcon";

/**
 * Normalize contact fields coming from different API shapes.
 */
export function resolveContactDetails(data = {}) {
  const d = data || {};

  const contactName =
    d.contact_name ??
    d.main_contact_name ??
    d.contact_person_name ??
    d.owner_name ??
    d.seller_name ??
    d.full_name ??
    d.supplier_name ??
    "";

  const contactPhone =
    d.contact_phone ??
    d.phone ??
    d.seller_phone ??
    d.user_phone ??
    d.phone_number ??
    "";

  const contactLocation =
    d.meeting_location ??
    d.offer_location ??
    d.meeting_place ??
    d.address ??
    d.location ??
    d.contact_location ??
    "";

  return {
    contactName,
    contactPhone,
    contactLocation,
    hasContactDetails: Boolean(contactName || contactPhone || contactLocation),
  };
}

/** ✅ אותם SVG-ים כמו ב-CarCard (כדי שהכרטיס ייראה זהה) */
function ContactPhoneIcon({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.9.35 1.78.68 2.62a2 2 0 0 1-.45 2.11L8.1 9.64a16 16 0 0 0 6.26 6.26l1.19-1.19a2 2 0 0 1 2.11-.45c.84.33 1.72.56 2.62.68A2 2 0 0 1 22 16.92Z"
        stroke="#7CB342"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** ✅ אותו כרטיס כמו ב-CarCard */
function ContactDetailsCard({
  contactName,
  contactPhone,
  contactLocation, // נשאר פה ל-future, כמו אצלך
  onClose = null,
  compact = false,
}) {
  return (
    <div
      dir="rtl"
      style={{ backgroundColor: "white" }}
      className={`relative rounded-[18px] border border-gray-200 bg-white shadow-[0_10px_30px_rgba(0,0,0,0.14)] ${
        compact ? "w-[320px] p-4" : "w-[360px] max-w-[92vw] p-5"
      }`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {onClose && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          className="absolute left-3 top-3 text-gray-400 hover:text-gray-600 text-[28px] leading-none"
          aria-label="סגירה"
        >
          ×
        </button>
      )}

      <div
        style={{ backgroundColor: "white", fontWeight: "500" }}
        className="text-center font-heebo text-black text-[18px]"
      >
        פרטי התקשרות
      </div>

      <div className="relative mt-4">
        <div className="space-y-2 pl-14">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 shrink-0">
              <ContactUserIcon />
              <span className="font-heebo text-black text-[18px] font-medium">שם איש קשר:</span>
            </div>
            <span className="font-heebo text-[16px] text-black text-left break-words">
              {contactName || "-"}
            </span>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 shrink-0">
              <ContactPhoneIcon />
              <span className="font-heebo text-black text-[18px] font-medium">פלאפון:</span>
            </div>
            <span className="font-heebo text-[16px] text-black text-left" dir="ltr">
              {contactPhone || "-"}
            </span>
          </div>

          {/* אם בעתיד תרצי גם מיקום בכרטיס – תגידי ואני אוסיף בדיוק כמו העיצוב */}
        </div>
      </div>
    </div>
  );
}

/**
 * ✅ ContactDetails – עכשיו זהה ל-CarCard:
 * Desktop: hover tooltip (מרכזי, absolute inset-0)
 * Mobile: click modal עם רקע מושחר + blur
 */
export default function ContactDetails({
  data,
  className = "",
  buttonLabel = "פרטי התקשרות",
}) {
  const { contactName, contactPhone, contactLocation, hasContactDetails } = useMemo(
    () => resolveContactDetails(data),
    [data]
  );

  const [showTooltip, setShowTooltip]= useState(false);
  const [showModal, setShowModal] = useState(false);
  const [canHover, setCanHover] = useState(false);

  // detect hover capability (desktop)
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setCanHover(mq.matches);
    update();

    if (mq.addEventListener) {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    } else {
      mq.addListener(update);
      return () => mq.removeListener(update);
    }
  }, []);

  // close modal on Escape
  useEffect(() => {
    if (!showModal) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowModal(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showModal]);

  const openContactDetails = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!hasContactDetails) return;

    // ✅ במובייל פותחים popup (זהה ל-CarCard)
    if (!canHover) setShowModal(true);
  };

  const closeContactDetails = () => setShowModal(false);

  const onContactMouseEnter = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (canHover && hasContactDetails) setShowTooltip(true);
  };

  const onContactMouseLeave = () => {
    if (canHover) setShowTooltip(false);
  };

  return (
    <>
      <div
        className={className}
        onClick={(e) => {
          // ✅ זהה ל-CarCard: לא לתת לקליק “לברוח” לקונטיינר
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div
          className="relative"
          onMouseEnter={onContactMouseEnter}
          onMouseLeave={onContactMouseLeave}
        >
          {/* ✅ טריגר זהה ל-CarCard */}
          <span
            type="button"
            onClick={openContactDetails}
            className={`min-w-[140px] font-assistant font-medium flex items-center justify-center gap-2 ${
              hasContactDetails ? "cursor-pointer" : "opacity-60 cursor-not-allowed"
            }`}
            aria-label="פרטי התקשרות"
          >
            <ContactUserIcon />
            <span className="text-black-900 text-[15px]">{buttonLabel}</span>
          </span>

          {/* ✅ Tooltip בדסקטופ – זהה ל-CarCard */}
          {canHover && showTooltip && hasContactDetails && (
            <div className="absolute inset-0 z-[2000] flex items-center justify-center pointer-events-none">
              <div
                className="pointer-events-auto"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <ContactDetailsCard
                  compact
                  contactName={contactName}
                  contactPhone={contactPhone}
                  contactLocation={contactLocation}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ✅ Popup במובייל – זהה ל-CarCard */}
      {showModal && !canHover && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
          style={{
            backgroundColor: "rgba(0,0,0,0.82)",
            backdropFilter: "blur(2px)",
          }}
          onClick={closeContactDetails}
        >
          <div
            className="relative"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <ContactDetailsCard
              contactName={contactName}
              contactPhone={contactPhone}
              contactLocation={contactLocation}
              onClose={closeContactDetails}
            />
          </div>
        </div>
      )}
    </>
  );
}